import React, { useState, useEffect } from 'react';
import { useSupabase } from '../../supabaseAuth';
import AdminUserDetailModal from './AdminUserDetailModal';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState({
    hasSubscriptions: '',
    dateFrom: '',
    dateTo: ''
  });
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    usersWithSubscriptions: 0
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSuspendDropdown, setShowSuspendDropdown] = useState(null);

  const { getAccessToken } = useSupabase();

  // Fetch users from API
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get the access token from Supabase auth
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';
      const queryParams = new URLSearchParams({
        search: searchTerm,
        sort_field: sortField,
        sort_direction: sortDirection,
        ...filters
      });

      // console.log('Making API call to:', `${apiBaseUrl}/api/admin/users?${queryParams}`);

      const response = await fetch(`${apiBaseUrl}/api/admin/users?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // console.log('API response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // console.log('API response data:', data);
      
      setUsers(data.users || []);
      setStats(data.stats || {});
    } catch (err) {
      setError(err.message);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // console.log('UsersPage mounted, fetching users...');
    fetchUsers();
  }, [searchTerm, sortField, sortDirection, filters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSuspendDropdown && !event.target.closest('.position-relative')) {
        setShowSuspendDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showSuspendDropdown]);

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Admin user management functions
  const toggleAdminStatus = async (userId, isCurrentlyAdmin) => {
    try {
      const token = await getAccessToken();
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';

      const response = await fetch(`${apiBaseUrl}/api/admin/users/${userId}/admin-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_admin: !isCurrentlyAdmin })
      });

      if (!response.ok) {
        throw new Error(`Failed to update admin status: ${response.status}`);
      }

      // Refresh data
      fetchUsers();
      return true;
    } catch (error) {
      console.error('Error updating admin status:', error);
      throw error;
    }
  };

  const suspendUser = async (userId, duration = '720h') => {
    try {
      const token = await getAccessToken();
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';

      const response = await fetch(`${apiBaseUrl}/api/admin/users/${userId}/suspend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ duration })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to suspend user: ${response.status}`);
      }

      const data = await response.json();
      
      // Refresh data
      fetchUsers();
      return data;
    } catch (error) {
      console.error('Error suspending user:', error);
      throw error;
    }
  };

  const unsuspendUser = async (userId) => {
    try {
      const token = await getAccessToken();
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';

      const response = await fetch(`${apiBaseUrl}/api/admin/users/${userId}/unsuspend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to unsuspend user: ${response.status}`);
      }

      const data = await response.json();
      
      // Refresh data
      fetchUsers();
      return data;
    } catch (error) {
      console.error('Error unsuspending user:', error);
      throw error;
    }
  };

  // Handle detail modal
  const handleViewDetails = (user, event) => {
    // Remove focus from button to prevent it staying blue
    if (event && event.target) {
      event.target.blur();
    }
    setSelectedUser(user);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedUser(null);
  };

  const exportUsers = async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';
      const response = await fetch(`${apiBaseUrl}/api/admin/users/export`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message);
      console.error('Error exporting users:', err);
    }
  };

  const refreshData = () => {
    fetchUsers();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-people me-2"></i>
            Users
          </h2>
          <p className="text-muted mb-0">
            Manage and view all user accounts
          </p>
        </div>
        <div className="btn-group">
          <button 
            className="btn btn-outline-primary"
            onClick={refreshData}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Refresh
          </button>
          <button 
            className="btn btn-success"
            onClick={exportUsers}
            disabled={loading}
          >
            <i className="bi bi-download me-2"></i>
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="card-title">Total Users</h6>
                  <h3 className="mb-0">{stats.totalUsers}</h3>
                </div>
                <div className="align-self-center">
                  <i className="bi bi-people display-6"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-success text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="card-title">Active This Month</h6>
                  <h3 className="mb-0">{stats.activeUsers}</h3>
                </div>
                <div className="align-self-center">
                  <i className="bi bi-person-check display-6"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-info text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="card-title">With Subscriptions</h6>
                  <h3 className="mb-0">{stats.usersWithSubscriptions}</h3>
                </div>
                <div className="align-self-center">
                  <i className="bi bi-credit-card display-6"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Search Users</label>
              <input 
                type="text"
                className="form-control"
                placeholder="Search by name, email, or ID..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Has Subscriptions</label>
              <select 
                className="form-select"
                value={filters.hasSubscriptions}
                onChange={(e) => handleFilterChange('hasSubscriptions', e.target.value)}
              >
                <option value="">All Users</option>
                <option value="true">With Subscriptions</option>
                <option value="false">Without Subscriptions</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Registration From</label>
              <input 
                type="date"
                className="form-control"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Registration To</label>
              <input 
                type="date"
                className="form-control"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Error loading users: {error}
        </div>
      )}

      {/* Users Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('id')}
                  >
                    ID
                    {sortField === 'id' && (
                      <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('email')}
                  >
                    Email
                    {sortField === 'email' && (
                      <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('first_name')}
                  >
                    Name
                    {sortField === 'first_name' && (
                      <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th>Location</th>
                  <th>Subscriptions</th>
                  <th>Flags</th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('created_at')}
                  >
                    Registered
                    {sortField === 'created_at' && (
                      <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      <i className="bi bi-inbox display-4 d-block mb-2"></i>
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <code className="text-muted">#{user.id}</code>
                      </td>
                      <td>
                        <div>
                          <strong>{user.email}</strong>
                          {user.supabase_id && (
                            <>
                              <br />
                              <small className="text-muted">
                                Supabase: {user.supabase_id.substring(0, 8)}...
                              </small>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>
                          {user.first_name || user.last_name ? (
                            <strong>{user.first_name} {user.last_name}</strong>
                          ) : (
                            <span className="text-muted">No name provided</span>
                          )}
                          {user.phone && (
                            <>
                              <br />
                              <small className="text-muted">{user.phone}</small>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>
                          {user.city && user.state ? (
                            <>
                              <strong>{user.city}, {user.state}</strong>
                              <br />
                              <small className="text-muted">{user.country || 'US'}</small>
                            </>
                          ) : (
                            <span className="text-muted">No location</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${user.subscription_count > 0 ? 'bg-success' : 'bg-secondary'}`}>
                          {user.subscription_count || 0} subscription{user.subscription_count !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {user.is_admin && (
                            <span className="badge bg-warning">
                              <i className="bi bi-shield-check me-1"></i>
                              Admin
                            </span>
                          )}
                          {user.is_suspended && (
                            <span className="badge bg-danger">
                              <i className="bi bi-ban me-1"></i>
                              Suspended
                            </span>
                          )}
                          {!user.is_admin && !user.is_suspended && (
                            <span className="badge bg-light text-dark">
                              <i className="bi bi-person me-1"></i>
                              User
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <small className="text-muted">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </small>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm position-relative">
                          <button 
                            className="btn btn-outline-primary"
                            title="View Details"
                            onClick={(e) => handleViewDetails(user, e)}
                            style={{ 
                              outline: 'none !important', 
                              boxShadow: 'none !important',
                              backgroundColor: 'transparent !important',
                              borderColor: '#6c757d !important'
                            }}
                            onFocus={(e) => e.target.blur()}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          <button 
                            className={`btn ${user.is_admin ? 'btn-outline-warning' : 'btn-outline-success'}`}
                            title={user.is_admin ? 'Remove Admin' : 'Make Admin'}
                            onClick={() => {
                              const action = user.is_admin ? 'remove admin privileges from' : 'grant admin privileges to';
                              if (window.confirm(`Are you sure you want to ${action} ${user.email}?`)) {
                                toggleAdminStatus(user.id, user.is_admin).catch(err => 
                                  alert('Failed to update admin status: ' + err.message)
                                );
                              }
                            }}
                          >
                            <i className={`bi ${user.is_admin ? 'bi-shield-x' : 'bi-shield-check'}`}></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            title="Suspend User"
                            onClick={() => setShowSuspendDropdown(showSuspendDropdown === user.id ? null : user.id)}
                          >
                            <i className="bi bi-ban"></i>
                          </button>
                          {showSuspendDropdown === user.id && (
                            <div 
                              className="dropdown-menu show position-absolute"
                              style={{ 
                                top: '100%', 
                                right: 0, 
                                left: 'auto',
                                zIndex: 1000,
                                minWidth: '200px'
                              }}
                            >
                              {user.is_suspended ? (
                                // If user is suspended, only show unsuspend option
                                <button
                                  className="dropdown-item text-success"
                                  onClick={() => {
                                    setShowSuspendDropdown(null);
                                    if (window.confirm(`Remove suspension for ${user.email}?`)) {
                                      unsuspendUser(user.id).catch(err => 
                                        alert('Failed to unsuspend user: ' + err.message)
                                      );
                                    }
                                  }}
                                >
                                  <i className="bi bi-check-circle me-2"></i>Remove Suspension
                                </button>
                              ) : (
                                // If user is not suspended, show suspension options
                                <>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => {
                                      setShowSuspendDropdown(null);
                                      if (window.confirm(`Suspend ${user.email} for 1 day?`)) {
                                        suspendUser(user.id, '24h').catch(err => 
                                          alert('Failed to suspend user: ' + err.message)
                                        );
                                      }
                                    }}
                                  >
                                    <i className="bi bi-clock me-2"></i>1 Day
                                  </button>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => {
                                      setShowSuspendDropdown(null);
                                      if (window.confirm(`Suspend ${user.email} for 1 week?`)) {
                                        suspendUser(user.id, '168h').catch(err => 
                                          alert('Failed to suspend user: ' + err.message)
                                        );
                                      }
                                    }}
                                  >
                                    <i className="bi bi-calendar-week me-2"></i>1 Week
                                  </button>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => {
                                      setShowSuspendDropdown(null);
                                      if (window.confirm(`Suspend ${user.email} for 30 days?`)) {
                                        suspendUser(user.id, '720h').catch(err => 
                                          alert('Failed to suspend user: ' + err.message)
                                        );
                                      }
                                    }}
                                  >
                                    <i className="bi bi-calendar-month me-2"></i>30 Days
                                  </button>
                                  <button
                                    className="dropdown-item text-danger"
                                    onClick={() => {
                                      setShowSuspendDropdown(null);
                                      if (window.confirm(`PERMANENTLY suspend ${user.email}?`)) {
                                        suspendUser(user.id, '87600h').catch(err => 
                                          alert('Failed to suspend user: ' + err.message)
                                        );
                                      }
                                    }}
                                  >
                                    <i className="bi bi-x-octagon me-2"></i>Permanent
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* User Detail Modal */}
      {selectedUser && (
        <AdminUserDetailModal
          show={showDetailModal}
          onHide={handleCloseDetailModal}
          user={selectedUser}
          onToggleAdmin={toggleAdminStatus}
        />
      )}
    </div>
  );
};

export default UsersPage;

